'use client';

import { motion, useReducedMotion } from 'framer-motion';

import { cn } from '@/lib/utils';
import { FADE_EASE } from '@/components/global/utils/FadeIn';

import type { ILeaderboardColumns } from '@/sanity/pages/PageLeaderboardTemplate';

import type { LeaderboardEntry } from './types';

const RANK_COL = 'w-[5.5rem] shrink-0';
const POINTS_COL = 'w-[14.75rem] shrink-0';
const WIN_COL = 'w-[13rem] shrink-0';
const TRADES_COL = 'w-[11.5rem] shrink-0';
const PF_COL = 'w-[13.5rem] shrink-0';

const ROW = 'flex items-center gap-[1.75rem] px-[3rem]';
const VAL = 'font-geist text-[2rem] font-medium leading-[3rem] tabular-nums text-white/75';

const RANK_COLOR: Record<number, string> = { 1: '#ffd36e', 2: '#dbe5ec', 3: '#e3b991' };

const ROWS_IN = { hidden: {}, show: { transition: { staggerChildren: 0.03 } } };
const ROW_IN = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: FADE_EASE } },
};

function initial(name: string): string {
  return name.trim().charAt(0).toUpperCase();
}

function Avatar({ name }: { name: string }) {
  return (
    <div className="relative flex h-[5rem] w-[5rem] shrink-0 items-center justify-center rounded-full border border-white/[0.22]">
      <span className="font-geist text-[1.875rem] font-medium leading-none text-white/60">
        {initial(name)}
      </span>
    </div>
  );
}

function Row({ entry }: { entry: LeaderboardEntry }) {
  return (
    <motion.div variants={ROW_IN} className={cn(ROW, 'h-[6.5rem] shrink-0 bg-white/5')}>
      <span
        className={cn(RANK_COL, 'font-jetbrainsmono text-[1.75rem] leading-[2.625rem] tabular-nums')}
        style={{ color: RANK_COLOR[entry.rank] ?? 'rgba(255,255,255,0.4)' }}
      >
        {entry.rank}
      </span>

      <Avatar name={entry.name} />

      <div className="flex min-w-0 flex-1 items-center pl-[1.5rem]">
        <span className="truncate font-geist text-[2rem] font-medium leading-[3rem] text-white">
          {entry.name}
        </span>
      </div>

      <span className={cn(POINTS_COL, VAL)}>{entry.points.toLocaleString('en-US')}</span>
      <span className={cn(WIN_COL, VAL)}>{entry.winrate.toFixed(1)}%</span>
      <span className={cn(TRADES_COL, VAL)}>{entry.trades.toLocaleString('en-US')}</span>
      <span className={cn(PF_COL, VAL, 'text-[#ffd36e]')}>{entry.profitFactor.toFixed(2)}</span>
    </motion.div>
  );
}

export function LeaderboardTable({
  entries,
  columns,
  view,
}: {
  entries: LeaderboardEntry[];
  columns?: ILeaderboardColumns;
  view?: string;
}) {
  const still = useReducedMotion();
  return (
    <div className="relative w-full">
      <div className="max-lg:overflow-x-auto max-lg:[scrollbar-width:none] max-lg:[&::-webkit-scrollbar]:hidden">
        <div className="max-lg:min-w-[150rem]">
          <motion.div
            key={view}
            variants={still ? undefined : ROWS_IN}
            initial={still ? undefined : 'hidden'}
            animate={still ? undefined : 'show'}
            className="flex h-[92.125rem] flex-col gap-[0.25rem] overflow-y-auto overflow-x-hidden rounded-[1rem] [scrollbar-width:thin]">
            <div
              className={cn(
                ROW,
                'sticky top-0 z-10 h-[5.125rem] shrink-0 bg-[#141414] font-jetbrainsmono text-[1.75rem] uppercase leading-[2.625rem] tracking-[0.0175rem] text-white/[0.55]',
              )}
            >
              <span className={RANK_COL}>{columns?.rank}</span>
              <span className="min-w-0 flex-1 pl-[8.25rem]">{columns?.trader}</span>
              <span className={POINTS_COL}>{columns?.points}</span>
              <span className={WIN_COL}>{columns?.winRate}</span>
              <span className={TRADES_COL}>{columns?.trades}</span>
              <span className={PF_COL}>{columns?.profitFactor}</span>
            </div>

            {entries.map((entry) => (
              <Row key={entry.rank} entry={entry} />
            ))}

            {entries.length === 0 && (
              <div className="flex items-center justify-center bg-white/5 px-[3rem] py-[8rem] text-center font-geist text-[2rem] text-white/50">
                No scores yet — check back once members start trading.
              </div>
            )}
          </motion.div>
        </div>
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[14.25rem] rounded-b-[1rem] bg-gradient-to-b from-transparent to-black to-[62%]" />
    </div>
  );
}
